import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Database, EyeSlash, Path, FileText } from "@phosphor-icons/react";

export default function Trust() {
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="trust-home-link">
            <div className="h-8 w-8 rounded-md bg-emerald-800 text-white grid place-items-center font-bold">A</div>
            <div className="leading-none">
              <div className="font-bold text-lg tracking-tight">Alaine's Bench<sup className="text-[0.5em] font-semibold ml-0.5 align-super">™</sup></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">Trust &amp; Security</div>
            </div>
          </Link>
          <Link to="/login" className="text-sm text-stone-500 hover:text-emerald-800" data-testid="trust-login-link">
            Sign in
          </Link>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-800 font-bold mb-3">Trust</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
          Your formulas are <span className="text-emerald-800">yours.</span>
        </h1>
        <p className="mt-5 text-stone-600 text-lg leading-relaxed max-w-2xl">
          Alaine's Bench was built by a food scientist who knows what it took to develop her own
          formulations. We treat yours the way we treat ours — as trade secrets.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid md:grid-cols-2 gap-5">
        <Pillar
          icon={<Lock size={20} weight="duotone" />}
          title="You own your data."
          body="Every formulation, ingredient cost, supplier note, and spec sheet you create remains your property. We claim no rights to it. If you cancel, you can export everything and delete your account — your data leaves with you."
          testId="pillar-ownership"
        />
        <Pillar
          icon={<EyeSlash size={20} weight="duotone" />}
          title="We will never sell or share your formulas."
          body="Your formulations are not training data. They are not aggregated and resold. They are not visible to other users. Period. The AI compliance assistant only sees what you explicitly send it for that conversation, and conversations are not used to improve any third-party model."
          testId="pillar-noshare"
        />
        <Pillar
          icon={<Database size={20} weight="duotone" />}
          title="Per-user isolation."
          body="Formulations, ingredient supplier prices, and business profiles are scoped to your account at the database level. Other users cannot see, query, or accidentally access your data — even with admin tooling, founder review of individual user data requires written consent."
          testId="pillar-isolation"
        />
        <Pillar
          icon={<ShieldCheck size={20} weight="duotone" />}
          title="Secure by default."
          body="JWT authentication with bcrypt-hashed passwords. HTTPS enforced. Production database hosted on encrypted infrastructure. Backups encrypted at rest. We follow OWASP guidance for everything we ship."
          testId="pillar-secure"
        />
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-lg border border-stone-200 bg-white p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <Path size={20} weight="duotone" className="text-emerald-800" />
            <h2 className="text-xl font-semibold text-stone-900">Security roadmap</h2>
          </div>
          <p className="text-stone-600 leading-relaxed">
            We're a small, fast team. We're transparent about what we have today and what's planned.
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Status state="now" label="HTTPS / TLS 1.3 in production" />
            <Status state="now" label="bcrypt password hashing (12 rounds)" />
            <Status state="now" label="JWT auth (httpOnly cookie + token mirror)" />
            <Status state="now" label="Per-user data isolation" />
            <Status state="now" label="Encrypted backups (daily)" />
            <Status state="now" label="Robots.txt blocks search indexing of the app" />
            <Status state="now" label="Tester NDA + Terms-of-Service binding" />
            <Status state="next" label="Two-factor authentication (Q2 2026)" />
            <Status state="next" label="Audit log of formulation changes (Q2 2026)" />
            <Status state="next" label="Customer-managed export to CSV / JSON (Q2 2026)" />
            <Status state="planned" label="SOC 2 Type I readiness review (Q4 2026)" />
            <Status state="planned" label="Single Sign-On (SSO) for team plans (2027)" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-lg border-2 border-emerald-700 bg-emerald-50/40 p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-stone-900">Founder commitment</h2>
          <p className="mt-3 text-stone-700 leading-relaxed">
            I'm Courtney. I built this tool because I needed it for my own brand
            (<a href="https://alainesfield.com" target="_blank" rel="noreferrer" className="text-emerald-800 underline">alainesfield.com</a>).
            Your trade secrets are sacred to me — my own depend on the same level of care. If anything
            on this page ever changes, you'll be notified by email at least 30 days before the change
            takes effect, and you'll have the right to export and delete your data first.
          </p>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Have a question or a security concern? Reach me directly at{" "}
            <Link to="/contact" className="text-emerald-800 underline">Contact us</Link>.
          </p>
          <div className="mt-4 text-xs text-stone-500">
            Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200 bg-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-stone-500 flex justify-between flex-wrap gap-4">
          <div>
            &copy; {new Date().getFullYear()} <b>Shirleys Daughter, LLC</b> · Alaine's Bench™
          </div>
          <div className="flex gap-4">
            <Link to="/changelog" className="hover:text-emerald-800" data-testid="trust-to-changelog">Changelog</Link>
            <Link to="/terms" className="hover:text-emerald-800">Terms</Link>
            <Link to="/privacy" className="hover:text-emerald-800">Privacy</Link>
            <Link to="/" className="hover:text-emerald-800">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Pillar({ icon, title, body, testId }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-6" data-testid={testId}>
      <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-800 grid place-items-center">
        {icon}
      </div>
      <div className="mt-3 font-semibold text-stone-900">{title}</div>
      <div className="text-sm text-stone-600 mt-2 leading-relaxed">{body}</div>
    </div>
  );
}

function Status({ state, label }) {
  const cfg = {
    now: { color: "text-emerald-800", bg: "bg-emerald-100", label: "Today" },
    next: { color: "text-amber-800", bg: "bg-amber-100", label: "Next" },
    planned: { color: "text-stone-600", bg: "bg-stone-100", label: "Planned" },
  }[state];
  return (
    <div className="flex items-center gap-2 py-1">
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} uppercase tracking-wider`}>
        {cfg.label}
      </span>
      <span className="text-stone-700">{label}</span>
    </div>
  );
}
